import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { track } from "@/lib/analytics";
import { Award, Crown, Sparkles, Star, Gift, Users, ArrowRight, Check, Instagram, Trophy } from "lucide-react";

const TIERS = [
  {
    key: "bronze",
    name: "Bronze",
    tagline: "The Spark",
    color: "#B87333",
    Icon: Star,
    minReferrals: 0,
    perks: [
      "Personal referral link + branded post templates",
      "5% credit on every friend's lock or purchase",
      "Free AI try-on credits (10/month)",
      "Bronze ambassador digital badge on your profile",
    ],
  },
  {
    key: "silver",
    name: "Silver",
    tagline: "The Influencer",
    color: "#A8ADB4",
    Icon: Award,
    minReferrals: 5,
    perks: [
      "Everything in Bronze",
      "10% credit on every referral",
      "Early access to new arrivals (48 hrs before public)",
      "Free curbside pickup + stylist appointment for you and 1 guest",
      "Feature slot on the school lookbook",
    ],
  },
  {
    key: "gold",
    name: "Gold",
    tagline: "The Trendsetter",
    color: "#D4AF37",
    Icon: Trophy,
    minReferrals: 15,
    perks: [
      "Everything in Silver",
      "15% credit + $100 sign-on gift card",
      "Free hemming or alterations (up to $50 value)",
      "Complimentary AI try-on for your whole squad (up to 4)",
      "Invite to the annual Heather's runway night",
      "Custom promo code for followers",
    ],
  },
  {
    key: "platinum",
    name: "Platinum",
    tagline: "The Icon",
    color: "#E5E4E2",
    Icon: Crown,
    minReferrals: 40,
    perks: [
      "Everything in Gold",
      "20% credit + $250 sign-on gift card",
      "Free personal stylist for the season (unlimited appointments)",
      "Head-to-toe complimentary prom look (dress OR tux + accessories, up to $700)",
      "Featured on the boutique homepage, IG, and TikTok",
      "First-name relationship with Heather herself",
      "Squad prom-suite: private after-hours session for you + 8 friends",
    ],
  },
];

export default function Ambassadors() {
  return (
    <div data-testid="ambassadors-page" className="pb-24">
      <Seo
        title="Become a Heather's Ambassador"
        description="Represent Heather's at your WI high school. Earn credits, perks, gift cards & free styling across our 4-tier ambassador ladder."
        path="/ambassadors"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-ink via-[#1a0e0f] to-ink text-cream">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(800px 300px at 80% 20%, rgba(230,196,143,0.35), transparent)" }} />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-rosemetal/20 blur-3xl" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-24 text-center">
          <motion.div
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 15 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-champagne/20 border border-champagne/40 mb-6"
          >
            <Crown className="w-7 h-7 text-champagne" strokeWidth={1.5} />
          </motion.div>
          <p className="font-label text-[10px] text-champagne tracking-[0.3em] mb-4">THE AMBASSADOR LADDER · 2026 SEASON</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl mx-auto">
            Be the <span className="italic text-champagne">face</span> of prom<br />at your school.
          </h1>
          <p className="font-body text-sm md:text-base text-cream/70 max-w-xl mx-auto mt-6">
            Four tiers. Real rewards. Bronze to Platinum — earn credit, exclusive access, and become the girl (or guy) your friends copy in the group chat.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/auth" data-testid="ambassadors-apply-cta" onClick={() => track("ambassadors_apply_cta_click", { location: "hero" })} className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-full tracking-widest hover:bg-cream transition-colors">
              APPLY TO BE AN AMBASSADOR
            </Link>
            <a href="#tiers" className="border border-cream/40 text-cream px-8 py-3.5 font-label text-[10px] rounded-full tracking-widest hover:bg-cream hover:text-ink transition-colors">
              SEE THE TIERS
            </a>
          </div>
        </div>
      </section>

      {/* TIER LADDER */}
      <section id="tiers" data-testid="tier-ladder" className="max-w-[1200px] mx-auto px-5 sm:px-8 mt-24">
        <p className="font-label text-[10px] text-rosemetal tracking-widest">THE LADDER</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">Climb the ranks.<br /><span className="italic text-rosemetal">Unlock the boutique.</span></h2>
        <p className="font-body text-sm text-muted-foreground mt-4 max-w-xl">
          Every friend you refer, every dress they lock, every try-on they share — moves you up the ladder. Perks stack. Once you level up, you stay leveled up through the season.
        </p>

        <div className="mt-14 space-y-8">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.key}
              data-testid={`tier-${t.key}`}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid md:grid-cols-[220px_1fr] gap-8 items-start border border-border rounded-sm overflow-hidden"
            >
              {/* connector line between tiers */}
              {i < TIERS.length - 1 && (
                <span className="hidden md:block absolute left-[85px] top-full h-8 w-px bg-gradient-to-b from-rosemetal to-transparent" />
              )}
              {/* Left card: tier badge */}
              <div className="relative p-8 text-cream" style={{ background: `linear-gradient(135deg, ${t.color}dd 0%, ${t.color}88 100%)` }}>
                <div className="absolute inset-0 bg-ink/50" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-ink/40 border border-cream/30 flex items-center justify-center backdrop-blur">
                    <t.Icon className="w-6 h-6" strokeWidth={1.5} style={{ color: t.color }} />
                  </div>
                  <p className="font-label text-[9px] text-cream/60 tracking-widest mt-6">TIER {String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-display text-4xl mt-1 leading-none">{t.name}</h3>
                  <p className="font-display italic text-lg mt-1" style={{ color: t.color }}>{t.tagline}</p>
                  <div className="mt-5 inline-flex items-center gap-1.5 bg-cream/10 border border-cream/20 rounded-full px-3 py-1">
                    <Users className="w-3 h-3" strokeWidth={1.5} />
                    <span className="font-label text-[9px] tracking-widest">
                      {t.minReferrals === 0 ? "STARTING TIER" : `${t.minReferrals}+ REFERRALS`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: perks */}
              <div className="p-8">
                <p className="font-label text-[10px] text-muted-foreground tracking-widest mb-5">WHAT YOU UNLOCK</p>
                <ul className="space-y-3">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-blush flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-rosemetal" strokeWidth={2.5} />
                      </span>
                      <span className="font-body text-sm text-ink">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 mt-24">
        <p className="font-label text-[10px] text-rosemetal tracking-widest">HOW YOU EARN</p>
        <h2 className="font-display text-4xl mt-2 leading-tight">Three ways to level up.</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {[
            { Icon: Users, t: "Refer friends", d: "Share your ambassador link. Every friend who locks a dress or tux counts toward your tier." },
            { Icon: Instagram, t: "Post + tag", d: "Post your Heather's try-ons and tag @heathersboutique. Every real story counts as one credit." },
            { Icon: Sparkles, t: "Show up", d: "Bring the boutique energy to school events. In-person appearances get you a bonus bump." },
          ].map((f, i) => (
            <div key={i} className="border border-border rounded-sm p-6 bg-cream">
              <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center mb-4">
                <f.Icon className="w-4 h-4 text-rosemetal" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl">{f.t}</h3>
              <p className="font-body text-sm text-muted-foreground mt-2">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 mt-24 text-center">
        <Gift className="w-10 h-10 mx-auto text-rosemetal mb-5" strokeWidth={1.5} />
        <h2 className="font-display text-4xl sm:text-5xl leading-tight">The ladder starts at Bronze.<br /><span className="italic text-rosemetal">You end wherever you're brave enough to climb.</span></h2>
        <p className="font-body text-sm text-muted-foreground max-w-xl mx-auto mt-5">
          One quick application. If you're at a verified Wisconsin school and have an Instagram/TikTok, you're in. Level up on your own timeline.
        </p>
        <Link to="/auth" data-testid="ambassadors-final-cta" onClick={() => track("ambassadors_apply_cta_click", { location: "final" })} className="mt-8 inline-flex items-center gap-2 bg-ink text-cream px-8 py-3.5 font-label text-[10px] rounded-full tracking-widest hover:bg-rosemetal transition-colors">
          APPLY NOW <ArrowRight className="w-3 h-3" />
        </Link>
      </section>
    </div>
  );
}
